import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Copy, ExternalLink, RotateCcw, TriangleAlert } from 'lucide-react';
import {
  BoutonMint,
  BoutonPrincipal,
  BoutonSecondaire,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { Modale } from '../components/ui/Modale';
import { ChampFiltre, ChampRecherche, Onglets, PanneauOnglet } from '../components/ui/Onglets';
import { BandeauExpedition } from '../components/notifications/BandeauExpedition';
import { useToast } from '../components/ui/useToast';
import { usePerimetre } from '../site/usePerimetre';
import { ErreurApi } from '../lib/api';
import { useTaillePage } from '../lib/useTaillePage';
import { dateReunion, jourCourt } from '../lib/useFormateurs';
import {
  usePreinscriptions,
  useRefuserPreinscription,
  useRenvoyerDecision,
  useValiderPreinscription,
  type CanalPrevenu,
  type LignePreinscription,
  type ModeDossier,
  type ReponsePreinscriptions,
  type StatutPreinscription,
} from '../lib/usePreinscriptions';

type Vue = 'A_TRAITER' | 'HISTORIQUE';
type PeriodeHistorique = 'TOUTES' | '30J' | '12M';

const LIBELLES_CANAL: Record<CanalPrevenu, string> = { EMAIL: 'e-mail', SMS: 'SMS', IN_APP: 'espace' };

/** « par e-mail et SMS » : ce que l'API annonce pour la décision ; `null` quand rien ne partirait. */
function parCanaux(canaux: CanalPrevenu[]): string | null {
  return canaux.length === 0 ? null : `par ${canaux.map((canal) => LIBELLES_CANAL[canal]).join(' et ')}`;
}

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

function pluriel(nombre: number, singulier: string, plurielTexte = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielTexte : singulier}`;
}

/** Recherche sans casse ni accent : « Begue » trouve « Bègue ». */
function cleRecherche(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function initiales(ligne: LignePreinscription): string {
  return `${ligne.prenom.charAt(0)}${ligne.nom.charAt(0)}`.toUpperCase();
}

/**
 * Préinscriptions en ligne (phase 5, TF-429) : la file des demandes déposées
 * par le formulaire public, que la direction valide — le dossier `INSCRIT`
 * naît et entre dans « Inscriptions & entretiens » — ou refuse avec un motif ;
 * puis l'historique des décisions.
 *
 * L'écran ne décide rien : doublons possibles, refus et messages au candidat
 * viennent de `/api/preinscriptions`.
 */
export function DirPreinscriptionsPage(): JSX.Element {
  const { site } = usePerimetre();
  const { data, isLoading, error } = usePreinscriptions(site);
  const [vue, setVue] = useState<Vue>('A_TRAITER');
  const [aValider, setAValider] = useState<LignePreinscription | null>(null);
  const [aRefuser, setARefuser] = useState<LignePreinscription | null>(null);
  const compteurs = data?.compteurs;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Préinscriptions"
        sousTitre="Demandes déposées en ligne · validation ou refus motivé, candidat prévenu"
        actions={<BarreSites />}
      />

      {data && <CarteLien lien={data.lienFormulaire} />}
      <BandeauExpedition
        etat={data?.expedition}
        contexte="Le candidat reçoit la décision par e-mail et SMS : ce qui n’est pas parti se renvoie depuis l’historique."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Compteur
          libelle="À traiter"
          valeur={compteurs?.aTraiter ?? '—'}
          ton={compteurs && compteurs.aTraiter > 0 ? 'warm' : 'navy'}
          detail={
            compteurs?.plusAncienneJours === null || compteurs === undefined
              ? 'Aucune demande en attente'
              : compteurs.plusAncienneJours === 0
                ? 'La plus ancienne déposée aujourd’hui'
                : `La plus ancienne déposée il y a ${pluriel(compteurs.plusAncienneJours, 'jour')}`
          }
        />
        <Compteur
          libelle="Doublons possibles"
          valeur={compteurs?.doublons ?? '—'}
          ton={compteurs && compteurs.doublons > 0 ? 'red' : 'navy'}
          detail="Dossier ou préinscription qui ressemble"
        />
        <Compteur libelle="Validées" valeur={compteurs?.validees ?? '—'} ton="mint" detail="Dossier créé" />
        <Compteur
          libelle="Refusées"
          valeur={compteurs?.refusees ?? '—'}
          ton="blue"
          detail="Motif adressé au candidat"
        />
      </div>

      {isLoading && <Chargement libelle="Chargement des préinscriptions…" />}
      {error && <MessageErreur message={messageErreur(error, 'Les préinscriptions n’ont pas pu être chargées.')} />}

      {data && (
        <>
          {vue === 'A_TRAITER' ? (
            <>
              <Onglets
                etiquette="Préinscriptions"
                onglets={[
                  { cle: 'A_TRAITER', libelle: 'À traiter', compte: data.aTraiter.length },
                  { cle: 'HISTORIQUE', libelle: 'Historique des décisions', compte: data.historique.length },
                ]}
                actif={vue}
                onChanger={setVue}
                idPanneau="panneau-preinscriptions"
              />
              <PanneauOnglet id="panneau-preinscriptions" actif={vue}>
                <FileATraiter lignes={data.aTraiter} onValider={setAValider} onRefuser={setARefuser} />
              </PanneauOnglet>
            </>
          ) : (
            <Historique donnees={data} vue={vue} onVue={setVue} />
          )}
        </>
      )}

      {data && aValider && (
        <ModaleValidation ligne={aValider} options={data.options} onFermer={() => setAValider(null)} />
      )}
      {data && aRefuser && (
        <ModaleRefus ligne={aRefuser} motifs={data.options.motifsProposes} onFermer={() => setARefuser(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lien du formulaire
// ---------------------------------------------------------------------------

/** Le lien se partage à la main : il est ici, prêt à copier. */
function CarteLien({ lien }: { lien: string }): JSX.Element {
  const toast = useToast();
  const champ = useRef<HTMLInputElement>(null);

  const copier = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(lien);
      toast('Lien du formulaire copié');
    } catch {
      // Sans presse-papiers (page non sécurisée, refus du navigateur), le lien est sélectionné.
      champ.current?.select();
      toast('Lien sélectionné : copiez-le avec Ctrl+C');
    }
  };

  return (
    <section className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-card sm:p-5 xl:flex-row xl:items-center xl:justify-between">
      <div className="min-w-0 xl:flex-1">
        <h2 className="font-display text-base font-bold text-codialis-navy">Formulaire de préinscription</h2>
        <p className="text-xs text-codialis-gray">
          À partager avec les candidats : il s’ouvre sans compte. Chaque demande arrive ici, et le candidat reçoit un
          accusé de réception par e-mail.
        </p>
      </div>
      <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center xl:shrink-0">
        <label className="min-w-0 sm:flex-1 xl:w-64 xl:flex-none">
          <span className="sr-only">Adresse du formulaire</span>
          <input
            ref={champ}
            readOnly
            value={lien}
            onFocus={(evenement) => evenement.target.select()}
            className="min-h-[44px] w-full truncate rounded-lg border border-gray-200 bg-gray-50 px-3 text-xs text-codialis-navy focus:outline-none focus:ring-2 focus:ring-codialis-mint"
          />
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void copier()}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-xl bg-codialis-navy px-4 text-sm font-semibold text-white transition hover:bg-codialis-deep sm:flex-none"
          >
            <Copy className="h-4 w-4" aria-hidden />
            Copier le lien
          </button>
          <a
            href={lien}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50 sm:flex-none"
          >
            <ExternalLink className="h-4 w-4" aria-hidden />
            Ouvrir
          </a>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// File à traiter
// ---------------------------------------------------------------------------

function FileATraiter({
  lignes,
  onValider,
  onRefuser,
}: {
  lignes: LignePreinscription[];
  onValider: (ligne: LignePreinscription) => void;
  onRefuser: (ligne: LignePreinscription) => void;
}): JSX.Element {
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();
  useEffect(() => setPage(1), [taillePage, lignes.length]);

  if (lignes.length === 0) {
    return (
      <p className="rounded-2xl bg-white px-4 py-12 text-center text-sm text-codialis-gray shadow-card">
        Aucune préinscription en attente dans ce périmètre.
      </p>
    );
  }

  const affichees = lignes.slice((page - 1) * taillePage, page * taillePage);
  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-card">
      <ul className="grid grid-cols-1 divide-y divide-gray-100 lg:grid-cols-2 lg:divide-y-0">
        {affichees.map((ligne) => (
          <li
            key={ligne.id}
            className="flex min-w-0 flex-col gap-3 border-gray-100 p-4 sm:p-5 lg:border-b lg:odd:border-r"
          >
            <Identite ligne={ligne} />
            <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-xs sm:grid-cols-2">
              <Donnee terme="Parcours">{ligne.parcours}</Donnee>
              <Donnee terme="Formation souhaitée">{ligne.formation?.titre ?? 'À préciser'}</Donnee>
              <Donnee terme="Téléphone">
                <a href={`tel:${ligne.telephone.replace(/[^\d+]/g, '')}`} className="text-codialis-sky hover:underline">
                  {ligne.telephone}
                </a>
              </Donnee>
              <Donnee terme="E-mail">
                <a href={`mailto:${ligne.email}`} className="break-all text-codialis-sky hover:underline">
                  {ligne.email}
                </a>
              </Donnee>
              <Donnee terme="Date de naissance">
                {jourCourt(ligne.dateNaissance)} · {ligne.age} ans
              </Donnee>
              <Donnee terme="Déposée le">{dateReunion(ligne.deposeeLe)}</Donnee>
            </dl>
            {ligne.message && (
              <blockquote className="rounded-xl bg-codialis-sand px-3 py-2 text-xs text-codialis-navy">
                « {ligne.message} »
              </blockquote>
            )}
            <Doublons ligne={ligne} />
            <div className="mt-auto flex flex-wrap gap-2 pt-1">
              <BoutonMint onClick={() => onValider(ligne)}>Valider</BoutonMint>
              <BoutonSecondaire onClick={() => onRefuser(ligne)}>Refuser</BoutonSecondaire>
            </div>
          </li>
        ))}
      </ul>
      <Pagination
        page={page}
        pages={Math.max(1, Math.ceil(lignes.length / taillePage))}
        total={lignes.length}
        affiches={affichees.length}
        taillePage={taillePage}
        onPage={setPage}
        onChangerTaille={setTaillePage}
        uniteLabel="préinscription"
      />
    </div>
  );
}

function Identite({ ligne }: { ligne: LignePreinscription }): JSX.Element {
  return (
    <div className="flex min-w-0 items-start gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-codialis-sky to-codialis-blue text-xs font-bold text-white">
        {initiales(ligne)}
      </span>
      <div className="min-w-0">
        <div className="truncate font-semibold text-codialis-navy">
          {ligne.prenom} {ligne.nom}
        </div>
        <div className="flex flex-wrap items-center gap-1 pt-0.5">
          <PastilleSite site={ligne.site.code} />
          {ligne.doublons.length > 0 && <Pastille texte="Doublon possible" ton="danger" />}
        </div>
      </div>
    </div>
  );
}

function Donnee({ terme, children }: { terme: string; children: ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-codialis-gray">{terme}</dt>
      <dd className="text-codialis-navy">{children}</dd>
    </div>
  );
}

function Doublons({ ligne }: { ligne: LignePreinscription }): JSX.Element | null {
  if (ligne.doublons.length === 0) return null;
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
      <div className="flex items-center gap-1.5 font-semibold">
        <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden />
        Cette personne est peut-être déjà connue
      </div>
      <ul className="mt-2 space-y-1.5">
        {ligne.doublons.map((doublon) => (
          <li key={`${doublon.type}-${doublon.id}`} className="min-w-0">
            <span className="font-semibold">{doublon.nom}</span> · {doublon.detail} · {doublon.motifs.join(', ')}
            {doublon.lien && (
              <>
                {' '}
                <Link to={doublon.lien} className="font-semibold text-codialis-sky hover:underline">
                  Ouvrir le dossier
                </Link>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Historique des décisions
// ---------------------------------------------------------------------------

const CLASSE_TH = 'px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray xl:px-4';
const CLASSE_TD = 'px-3 py-3 align-top xl:px-4';

interface FiltresHistorique {
  recherche: string;
  decision: '' | StatutPreinscription;
  formation: string;
  periode: PeriodeHistorique;
}

const SANS_FILTRE: FiltresHistorique = { recherche: '', decision: '', formation: '', periode: 'TOUTES' };

function retenue(ligne: LignePreinscription, filtres: FiltresHistorique, maintenant: number): boolean {
  if (filtres.decision && ligne.statut !== filtres.decision) return false;
  if (
    filtres.formation === 'SANS'
      ? ligne.formation !== null
      : filtres.formation && ligne.formation?.code !== filtres.formation
  ) {
    return false;
  }
  if (filtres.periode !== 'TOUTES' && ligne.decision) {
    const jours = filtres.periode === '30J' ? 30 : 365;
    if (maintenant - new Date(ligne.decision.le).getTime() > jours * 86_400_000) return false;
  }
  const terme = cleRecherche(filtres.recherche);
  if (!terme) return true;
  return [ligne.nom, ligne.prenom, ligne.email, ligne.telephone].some((champ) => cleRecherche(champ).includes(terme));
}

function Historique({
  donnees,
  vue,
  onVue,
}: {
  donnees: ReponsePreinscriptions;
  vue: Vue;
  onVue: (vue: Vue) => void;
}): JSX.Element {
  const [filtres, setFiltres] = useState<FiltresHistorique>(SANS_FILTRE);
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();
  const changer = <C extends keyof FiltresHistorique>(cle: C, valeur: FiltresHistorique[C]): void =>
    setFiltres((avant) => ({ ...avant, [cle]: valeur }));

  const lignes = useMemo(() => {
    const maintenant = Date.now();
    return donnees.historique.filter((ligne) => retenue(ligne, filtres, maintenant));
  }, [donnees.historique, filtres]);
  useEffect(() => setPage(1), [filtres, taillePage]);
  const affichees = lignes.slice((page - 1) * taillePage, page * taillePage);
  const filtreActif = JSON.stringify(filtres) !== JSON.stringify(SANS_FILTRE);

  return (
    <>
      <Onglets
        etiquette="Préinscriptions"
        onglets={[
          { cle: 'A_TRAITER', libelle: 'À traiter', compte: donnees.aTraiter.length },
          { cle: 'HISTORIQUE', libelle: 'Historique des décisions', compte: donnees.historique.length },
        ]}
        actif={vue}
        onChanger={onVue}
        idPanneau="panneau-preinscriptions"
        actions={
          <>
            <ChampRecherche
              valeur={filtres.recherche}
              onChange={(valeur) => changer('recherche', valeur)}
              etiquette="Rechercher un candidat"
              placeholder="Nom, e-mail, téléphone…"
            />
            <ChampFiltre
              valeur={filtres.decision}
              onChange={(valeur) => changer('decision', valeur as FiltresHistorique['decision'])}
              etiquette="Décision"
            >
              <option value="">Toutes les décisions</option>
              <option value="VALIDEE">Validées</option>
              <option value="REFUSEE">Refusées</option>
            </ChampFiltre>
            <ChampFiltre
              valeur={filtres.formation}
              onChange={(valeur) => changer('formation', valeur)}
              etiquette="Formation"
            >
              <option value="">Toutes les formations</option>
              <option value="SANS">Formation à préciser</option>
              {donnees.options.formations.map((formation) => (
                <option key={formation.code} value={formation.code}>
                  {formation.titre}
                </option>
              ))}
            </ChampFiltre>
            <ChampFiltre
              valeur={filtres.periode}
              onChange={(valeur) => changer('periode', valeur as PeriodeHistorique)}
              etiquette="Période"
            >
              <option value="TOUTES">Toute période</option>
              <option value="30J">Décidées depuis 30 jours</option>
              <option value="12M">Décidées depuis 12 mois</option>
            </ChampFiltre>
            {filtreActif && (
              <button
                type="button"
                onClick={() => setFiltres(SANS_FILTRE)}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-codialis-sky hover:underline"
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                Réinitialiser
              </button>
            )}
          </>
        }
      />

      <PanneauOnglet id="panneau-preinscriptions" actif={vue} className="space-y-6">
        {lignes.length === 0 ? (
          <p className="rounded-2xl bg-white px-4 py-12 text-center text-sm text-codialis-gray shadow-card">
            {donnees.historique.length === 0
              ? 'Aucune décision n’a encore été prise dans ce périmètre.'
              : 'Aucune décision ne correspond aux filtres.'}
          </p>
        ) : (
          <div className="overflow-hidden rounded-2xl bg-white shadow-card">
            {/* Sous l'ordinateur, une carte par décision : le tableau y défilerait de côté. */}
            <ul className="grid grid-cols-1 divide-y divide-gray-100 md:grid-cols-2 md:divide-y-0 xl:hidden">
              {affichees.map((ligne) => (
                <li key={ligne.id} className="min-w-0 space-y-3 border-gray-100 p-4 md:border-b md:odd:border-r">
                  <Identite ligne={ligne} />
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Demande ligne={ligne} />
                    <Decision ligne={ligne} />
                    <Suite ligne={ligne} />
                    <Prevenu ligne={ligne} />
                  </div>
                </li>
              ))}
            </ul>
            <div className="relative hidden overflow-x-auto xl:block">
              <table className="w-full min-w-[900px] text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    {['Candidat', 'Demande', 'Décision', 'Motif ou dossier', 'Candidat prévenu'].map((colonne) => (
                      <th key={colonne} className={CLASSE_TH}>
                        {colonne}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {affichees.map((ligne) => (
                    <tr key={ligne.id} className="transition hover:bg-gray-50">
                      <td className={`${CLASSE_TD} max-w-[200px]`}>
                        <Identite ligne={ligne} />
                      </td>
                      <td className={`${CLASSE_TD} max-w-[200px]`}>
                        <Demande ligne={ligne} />
                      </td>
                      <td className={CLASSE_TD}>
                        <Decision ligne={ligne} />
                      </td>
                      <td className={`${CLASSE_TD} max-w-[260px]`}>
                        <Suite ligne={ligne} />
                      </td>
                      <td className={CLASSE_TD}>
                        <Prevenu ligne={ligne} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pages={Math.max(1, Math.ceil(lignes.length / taillePage))}
              total={lignes.length}
              affiches={affichees.length}
              taillePage={taillePage}
              onPage={setPage}
              onChangerTaille={setTaillePage}
              uniteLabel="décision"
            />
          </div>
        )}
      </PanneauOnglet>
    </>
  );
}

function Detail({ children }: { children: ReactNode }): JSX.Element {
  return <div className="text-[11px] text-codialis-gray">{children}</div>;
}

function Libelle({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="text-[11px] font-semibold uppercase tracking-wider text-codialis-gray xl:hidden">{children}</div>
  );
}

function Demande({ ligne }: { ligne: LignePreinscription }): JSX.Element {
  return (
    <div className="min-w-0 space-y-0.5 text-xs">
      <Libelle>Demande</Libelle>
      <div className="font-medium text-codialis-navy">{ligne.formation?.titre ?? 'Formation à préciser'}</div>
      <Detail>{ligne.parcours}</Detail>
      <Detail>Déposée le {dateReunion(ligne.deposeeLe)}</Detail>
    </div>
  );
}

function Decision({ ligne }: { ligne: LignePreinscription }): JSX.Element {
  return (
    <div className="min-w-0 space-y-1 text-xs">
      <Libelle>Décision</Libelle>
      <Pastille texte={ligne.statutLibelle} ton={ligne.statut === 'VALIDEE' ? 'success' : 'danger'} />
      {ligne.decision && (
        <Detail>
          Le {dateReunion(ligne.decision.le)}
          {ligne.decision.par ? ` par ${ligne.decision.par}` : ''}
        </Detail>
      )}
    </div>
  );
}

function Suite({ ligne }: { ligne: LignePreinscription }): JSX.Element {
  return (
    <div className="min-w-0 space-y-1 text-xs">
      <Libelle>{ligne.statut === 'REFUSEE' ? 'Motif' : 'Dossier'}</Libelle>
      {ligne.statut === 'REFUSEE' ? (
        <p className="whitespace-pre-line break-words text-codialis-navy">{ligne.decision?.motif}</p>
      ) : ligne.dossier ? (
        <div className="flex flex-wrap items-center gap-2">
          <Pastille texte={ligne.dossier.statutLibelle} ton={ligne.dossier.statutTon} />
          <Link
            to={`/adm-dossier/${ligne.dossier.id}`}
            className="inline-flex min-h-[36px] items-center whitespace-nowrap font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]"
          >
            Ouvrir le dossier
          </Link>
        </div>
      ) : (
        <Detail>Dossier supprimé depuis</Detail>
      )}
    </div>
  );
}

function Prevenu({ ligne }: { ligne: LignePreinscription }): JSX.Element {
  const toast = useToast();
  const renvoyer = useRenvoyerDecision();
  const relancer = (): void =>
    renvoyer.mutate(ligne.id, {
      onSuccess: (resultat) => toast(resultat.message),
      onError: (refus) => toast(messageErreur(refus, 'Le message n’a pas pu être renvoyé.')),
    });

  return (
    <div className="min-w-0 space-y-1 text-xs">
      <Libelle>Candidat prévenu</Libelle>
      {ligne.prevenu ? (
        <div className="text-codialis-navy">
          Le {dateReunion(ligne.prevenu.le)} par{' '}
          {ligne.prevenu.canaux.map((canal) => LIBELLES_CANAL[canal]).join(' et ')}
        </div>
      ) : (
        <Pastille texte="Non prévenu" ton="warning" />
      )}
      {ligne.refusRenvoi === null && (
        <button
          type="button"
          onClick={relancer}
          disabled={renvoyer.isPending}
          className="inline-flex min-h-[36px] items-center whitespace-nowrap font-semibold text-codialis-sky hover:underline disabled:opacity-60 [@media(pointer:coarse)]:min-h-[44px]"
        >
          {renvoyer.isPending ? 'Envoi…' : 'Renvoyer le message'}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Décisions
// ---------------------------------------------------------------------------

const CLASSE_CHAMP =
  'min-h-[44px] w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

function ModaleValidation({
  ligne,
  options,
  onFermer,
}: {
  ligne: LignePreinscription;
  options: ReponsePreinscriptions['options'];
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const valider = useValiderPreinscription();
  const annonce = parCanaux(ligne.canauxDecision);
  const [siteCode, setSiteCode] = useState(ligne.site.code);
  const [mode, setMode] = useState<ModeDossier>(ligne.mode);
  const [formationCode, setFormationCode] = useState(ligne.formation?.code ?? '');
  // Une formation retirée du catalogue depuis le dépôt reste proposée, pour être vue et remplacée.
  const formations =
    ligne.formation && !options.formations.some((formation) => formation.code === ligne.formation?.code)
      ? [...options.formations, ligne.formation]
      : options.formations;

  const confirmer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    valider.mutate(
      { id: ligne.id, corrections: { siteCode, mode, formationCode: formationCode || null } },
      {
        onSuccess: (resultat) => {
          toast(resultat.message);
          onFermer();
        },
      },
    );
  };

  return (
    <Modale
      titre={`Valider la préinscription de ${ligne.prenom} ${ligne.nom}`}
      sousTitre="Le dossier naît au statut « Inscrit » et entre dans « Inscriptions & entretiens »"
      ouverte
      onFermer={onFermer}
    >
      <form onSubmit={confirmer} className="space-y-4 p-4 sm:p-6">
        <Doublons ligne={ligne} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block min-w-0">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">Antenne</span>
            <select
              value={siteCode}
              onChange={(evenement) => setSiteCode(evenement.target.value)}
              className={CLASSE_CHAMP}
            >
              {options.antennes.map((antenne) => (
                <option key={antenne.code} value={antenne.code}>
                  {antenne.nom}
                </option>
              ))}
            </select>
          </label>
          <label className="block min-w-0">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
              Parcours
            </span>
            <select
              value={mode}
              onChange={(evenement) => setMode(evenement.target.value as ModeDossier)}
              className={CLASSE_CHAMP}
            >
              {options.parcours.map((parcours) => (
                <option key={parcours.valeur} value={parcours.valeur}>
                  {parcours.libelle}
                </option>
              ))}
            </select>
          </label>
          <label className="block min-w-0 sm:col-span-2">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
              Formation
            </span>
            <select
              value={formationCode}
              onChange={(evenement) => setFormationCode(evenement.target.value)}
              className={CLASSE_CHAMP}
            >
              <option value="">À préciser à l’entretien</option>
              {formations.map((formation) => (
                <option key={formation.code} value={formation.code}>
                  {formation.titre}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="text-xs text-codialis-gray">
          {annonce
            ? `${ligne.prenom} reçoit la confirmation ${annonce}`
            : `Aucun prestataire d’envoi n’est raccordé : la confirmation à ${ligne.prenom} est tracée sans quitter la plateforme`}{' '}
          ; le recrutement de l’antenne est prévenu pour l’entretien. La décision est définitive.
        </p>
        {valider.error && (
          <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800">
            {messageErreur(valider.error, 'La préinscription n’a pas pu être validée.')}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={valider.isPending}>
            {valider.isPending ? 'Validation…' : 'Valider et créer le dossier'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}

function ModaleRefus({
  ligne,
  motifs,
  onFermer,
}: {
  ligne: LignePreinscription;
  motifs: string[];
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const refuser = useRefuserPreinscription();
  const [motif, setMotif] = useState('');
  const annonce = parCanaux(ligne.canauxDecision);

  const confirmer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    refuser.mutate(
      { id: ligne.id, motif },
      {
        onSuccess: (resultat) => {
          toast(resultat.message);
          onFermer();
        },
      },
    );
  };

  return (
    <Modale
      titre={`Refuser la préinscription de ${ligne.prenom} ${ligne.nom}`}
      sousTitre="Le motif est adressé tel quel au candidat"
      ouverte
      onFermer={onFermer}
    >
      <form onSubmit={confirmer} className="space-y-4 p-4 sm:p-6">
        <div>
          <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            Motifs fréquents
          </span>
          <div className="flex flex-wrap gap-2">
            {motifs.map((propose) => (
              <button
                key={propose}
                type="button"
                onClick={() => setMotif(propose)}
                className="min-h-[36px] rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-left text-xs text-codialis-navy transition hover:bg-gray-50 [@media(pointer:coarse)]:min-h-[44px]"
              >
                {propose}
              </button>
            ))}
          </div>
        </div>
        <label className="block">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            Motif du refus
          </span>
          <textarea
            required
            rows={4}
            maxLength={1000}
            value={motif}
            onChange={(evenement) => setMotif(evenement.target.value)}
            className={`${CLASSE_CHAMP} resize-y`}
          />
        </label>
        <p className="text-xs text-codialis-gray">
          {annonce
            ? `${ligne.prenom} le reçoit ${annonce}, avec le téléphone de l’antenne pour toute question.`
            : `Aucun prestataire d’envoi n’est raccordé : le refus à ${ligne.prenom} est tracé sans quitter la plateforme.`}{' '}
          La décision est définitive : une nouvelle préinscription reste possible.
        </p>
        {refuser.error && (
          <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800">
            {messageErreur(refuser.error, 'La préinscription n’a pas pu être refusée.')}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={refuser.isPending || motif.trim().length < 5}>
            {refuser.isPending ? 'Envoi…' : 'Refuser la préinscription'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}
